// SixSigmaRNR.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import {
  VictoryChart,
  VictoryAxis,
  VictoryBar,
  VictoryLabel,
  VictoryTooltip,
} from "victory";
import './SigmaStudio.css';

const SixSigmaRNR = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [remarks, setRemarks] = useState("");
  const [partsList, setPartsList] = useState("");       // e.g. Part-1\nPart-2\n...
  const [operatorsList, setOperatorsList] = useState(""); // Operator A\nOperator B\n...
  const [replications, setReplications] = useState("3");   // usually 2 or 3
  const [measurementDataText, setMeasurementDataText] = useState("");
  const [tolerance, setTolerance] = useState("");         // USL - LSL (optional but recommended)

  const [showHelp, setShowHelp] = useState(false);
  const [rrResult, setRrResult] = useState(null);
  const [error, setError] = useState("");

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const populateExample = () => {
    setError("");
    setRrResult(null);

    setTitle("Outer Diameter Measurement System Check");
    setRemarks("3 operators measured 10 parts, 3 times each with digital micrometer");
    setPartsList("P1\nP2\nP3\nP4\nP5\nP6\nP7\nP8\nP9\nP10");
    setOperatorsList("Op-A\nOp-B\nOp-C");
    setReplications("3");
    setTolerance("0.05"); // example: tolerance = 0.05 mm
    setMeasurementDataText(
      "10.02,10.03,10.01   10.04,10.05,10.03   10.00,10.01,10.02\n" +
      "20.12,20.14,20.11   20.15,20.16,20.13   20.10,20.09,20.11\n" +
      "15.45,15.47,15.44   15.48,15.50,15.46   15.43,15.42,15.45\n" +
      // ... imagine 10 lines total (shortened here for demo)
      "25.67,25.68,25.66   25.70,25.71,25.69   25.65,25.64,25.66"
    );
  };

  const parseList = (str) => str.split('\n').map(l => l.trim()).filter(Boolean);

  const parseMeasurements = (text, nParts, nOperators, nRepls) => {
    const lines = text.trim().split('\n').filter(Boolean);
    if (lines.length !== nParts) return null;

    const data = [];
    for (let p = 0; p < nParts; p++) {
      const values = lines[p].split(/[\s,;\t]+/)
        .map(v => parseFloat(v.trim()))
        .filter(v => !isNaN(v));

      if (values.length !== nOperators * nRepls) return null;

      for (let o = 0; o < nOperators; o++) {
        for (let r = 0; r < nRepls; r++) {
          const idx = o * nRepls + r;
          data.push({
            part: p + 1,
            operator: o + 1,
            replication: r + 1,
            value: values[idx]
          });
        }
      }
    }
    return data;
  };

  const calculateRNR = () => {
    setError("");
    setRrResult(null);

    if (!title.trim()) return setError("Title is required.");

    const parts = parseList(partsList);
    const operators = parseList(operatorsList);
    const nParts = parts.length;
    const nOperators = operators.length;
    const nRepls = parseInt(replications, 10);

    if (nParts < 5) return setError("Use at least 5–10 parts for reliable result.");
    if (nOperators < 2) return setError("Minimum 2 operators required.");
    if (![2,3].includes(nRepls)) return setError("Use 2 or 3 replications (measurements per part per operator).");

    const measurements = parseMeasurements(measurementDataText, nParts, nOperators, nRepls);
    if (!measurements) return setError("Measurement data format invalid. Each line = one part, all operator readings separated by comma or space.");

    // ─────────────────────────────────────────────
    // Simple Range method calculations (most common for MSME)
    // ─────────────────────────────────────────────

    // 1. Average per part-operator combination
    const avgByCell = {};
    measurements.forEach(m => {
      const key = `${m.part}-${m.operator}`;
      if (!avgByCell[key]) avgByCell[key] = { sum: 0, count: 0 };
      avgByCell[key].sum += m.value;
      avgByCell[key].count++;
    });

    Object.keys(avgByCell).forEach(k => {
      avgByCell[k] = avgByCell[k].sum / avgByCell[k].count;
    });

    // 2. Range per part-operator (max - min per cell)
    const rangeByCell = {};
    measurements.forEach(m => {
      const key = `${m.part}-${m.operator}`;
      if (!rangeByCell[key]) rangeByCell[key] = { min: m.value, max: m.value };
      rangeByCell[key].min = Math.min(rangeByCell[key].min, m.value);
      rangeByCell[key].max = Math.max(rangeByCell[key].max, m.value);
    });
    Object.keys(rangeByCell).forEach(k => {
      rangeByCell[k] = rangeByCell[k].max - rangeByCell[k].min;
    });

    // 3. Average range per operator → Equipment Variation (EV)
    const avgRangePerOp = Array(nOperators).fill(0);
    for (let o = 1; o <= nOperators; o++) {
      let sumR = 0, cnt = 0;
      for (let p = 1; p <= nParts; p++) {
        const key = `${p}-${o}`;
        if (rangeByCell[key] !== undefined) {
          sumR += rangeByCell[key];
          cnt++;
        }
      }
      avgRangePerOp[o-1] = cnt > 0 ? sumR / cnt : 0;
    }
    const grandAvgRange = avgRangePerOp.reduce((a,b)=>a+b,0) / nOperators;

    // Constants for d2* (Range method) – approx values for common cases
    const d2star = { 2: 1.128, 3: 1.693 }[nRepls] || 1.128;
    const EV = grandAvgRange / d2star;

    // 4. Reproducibility (AV)
    const avgByPart = {};
    for (let p = 1; p <= nParts; p++) {
      let sum = 0, cnt = 0;
      for (let o = 1; o <= nOperators; o++) {
        const key = `${p}-${o}`;
        if (avgByCell[key] !== undefined) {
          sum += avgByCell[key];
          cnt++;
        }
      }
      avgByPart[p] = cnt > 0 ? sum / cnt : 0;
    }

    const rangeOfAverages = Math.max(...Object.values(avgByPart)) - Math.min(...Object.values(avgByPart));
    const AV_raw = rangeOfAverages / 1.693; // d2 for nOperators≈3
    const AV = Math.sqrt(Math.max(0, AV_raw**2 - EV**2 / (nParts * nRepls)));

    // 5. Total Gage R&R, Part-to-Part
    const allValues = measurements.map(m => m.value);
    const grandMean = allValues.reduce((a,b)=>a+b,0) / allValues.length;
    const totalVar = allValues.reduce((s,v)=>s + (v-grandMean)**2, 0) / (allValues.length - 1);
    const gageRnrVar = EV**2 + AV**2;
    const partVar = Math.max(0, totalVar - gageRnrVar / nRepls);

    // 6. % to total / tolerance
    const tv = Math.sqrt(totalVar);
    const gageRRPercent = tv > 0 ? (Math.sqrt(gageRnrVar) / tv) * 100 : 0;
    const ndc = tv > 0 ? Math.floor(1.41 * Math.sqrt(partVar / gageRnrVar)) : 0;

    let tolPercent = null;
    const tolVal = parseFloat(tolerance);
    if (!isNaN(tolVal) && tolVal > 0) {
      tolPercent = (Math.sqrt(gageRnrVar) * 6 / tolVal) * 100;
    }

    setRrResult({
      title,
      remarks,
      nParts,
      nOperators,
      nRepls,
      EV: EV.toFixed(4),
      AV: AV.toFixed(4),
      gageRnr: Math.sqrt(gageRnrVar).toFixed(4),
      partToPart: Math.sqrt(partVar).toFixed(4),
      totalVariation: tv.toFixed(4),
      gageRRPercent: gageRRPercent.toFixed(1),
      ndc,
      tolPercent: tolPercent ? tolPercent.toFixed(1) : null,
      interpretation: gageRRPercent <= 10 ? "Excellent" :
                      gageRRPercent <= 30 ? "Acceptable" : "Not Acceptable – Improve measurement system"
    });
  };

  const renderBarContribution = (result) => {
    if (!result) return null;
    const data = [
      { x: "Equipment\n(Repeatability)", y: parseFloat(result.EV)**2, fill: "#48bb78" },
      { x: "Appraiser\n(Reproducibility)", y: parseFloat(result.AV)**2, fill: "#3182ce" },
      { x: "Part-to-Part", y: parseFloat(result.partToPart)**2, fill: "#f56565" }
    ];

    return (
      <div style={{ height: "320px", width: "100%" }}>
        <VictoryChart
          height={320}
          padding={{ top: 40, bottom: 100, left: 80, right: 40 }}
          domainPadding={{ x: 30, y: 20 }}
        >
          <VictoryAxis
            style={{
              tickLabels: { fill: "#e2e8f0", fontSize: 11, angle: -30, textAnchor: "end" }
            }}
          />
          <VictoryAxis
            dependentAxis
            style={{ tickLabels: { fill: "#e2e8f0", fontSize: 11 } }}
            label="% Contribution to Variation"
            axisLabelComponent={<VictoryLabel dy={-30} />}
          />
          <VictoryBar
            data={data}
            barWidth={50}
            labels={({ datum }) => datum.y.toFixed(1)}
            style={{ data: { fill: ({ datum }) => datum.fill } }}
            labelComponent={<VictoryTooltip />}
          />
        </VictoryChart>
      </div>
    );
  };

  const renderHelp = () => (
    <div className="mt-6 p-6 bg-gray-900 rounded-xl border border-teal-900/50">
      <h2 className="text-xl lg:text-2xl font-bold text-yellow-300 mb-6 text-center">
        Help – Gage R&R for MSME Factories
      </h2>
      <p className="mb-4">
        Gage R&R checks if your measurement system (micrometer, caliper, gauge etc.) is good enough.
        Bad measurement system → wrong decisions about parts & processes.
      </p>
      <ul className="list-disc pl-6 space-y-2 mb-6">
        <li>Use 5–10 real parts covering your normal variation</li>
        <li>2–3 operators who normally use the instrument</li>
        <li>Each operator measures each part 2–3 times (blind – no seeing previous reading)</li>
        <li>Enter data row-by-row: one line = one part</li>
        <li>Separate readings with comma or space</li>
        <li>Enter tolerance (USL – LSL) if known – very helpful!</li>
      </ul>
      <p className="mb-4 font-medium text-teal-300">Decision guidelines:</p>
      <ul className="list-decimal pl-6 space-y-1">
        <li>% Gage R&R ≤ 10% → Excellent</li>
        <li>≤ 30% → Acceptable for most applications</li>
        <li>> 30% → Measurement system needs improvement</li>
        <li>Number of Distinct Categories (ndc) ≥ 5 → good</li>
      </ul>
      <div className="text-center mt-8">
        <button onClick={() => setShowHelp(false)} className="w-40 px-2 py-2 bg-teal-700 hover:bg-teal-600 rounded-lg font-medium">
          Close Help
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-black [&_p]:text-white [&_li]:text-gray-100 text-gray-100 font-sans">
      <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-yellow-200">
            📏 Think5Whys Sigma Studio
          </div>
          <div className="text-[11px] text-gray-200">Gage R&R – Measurement System Check for MSMEs</div>
        </div>

        <div className="hidden lg:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs lg:text-sm text-teal-300">{userName}</div>
              <button onClick={handleLogout} className="px-3 py-1 rounded-md bg-red-600 hover:bg-red-700 text-sm">
                ⏻ Logout
              </button>
            </>
          ) : (
            <button onClick={() => navigate("/login")} className="px-3 py-1 rounded border border-gray-600 text-sm">
              🔐 Login
            </button>
          )}
        </div>

        <button className="md:hidden text-2xl text-teal-300" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-b border-gray-700 px-4 py-4">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-4">
              <div className="text-teal-300">{userName}</div>
              <button onClick={handleLogout} className="px-6 py-2 bg-red-700 rounded">Logout</button>
            </div>
          ) : (
            <div className="text-center">
              <button onClick={() => navigate("/login")} className="px-6 py-2 border rounded">Login</button>
            </div>
          )}
        </div>
      )}

      <main className="flex-1 p-4 pb-12 max-w-6xl mx-auto">
        <div className="grid grid-cols-3 gap-2 place-items-center mb-4">
          <button onClick={populateExample} className="text-sm px-3 py-2 bg-green-700 hover:bg-green-600 rounded-md font-medium">
            Populate Example
          </button>
          <button onClick={() => setShowHelp(!showHelp)} className="text-sm px-3 py-2 bg-blue-700 hover:bg-blue-600 rounded-md font-medium">
            {showHelp ? "Hide" : "Show"} Help
          </button>
        </div>

        {showHelp && renderHelp()}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block mb-2 text-cyan-300 font-medium">Study Title</label>
            <input
              type="text"
              className="w-full p-3 bg-gray-900 border border-gray-700 rounded text-white"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Outer Diameter Measurement System Check"
            />
          </div>

          <div>
            <label className="block mb-2 text-cyan-300 font-medium">Tolerance (USL-LSL) – optional</label>
            <input
              type="text"
              className="w-full p-3 bg-gray-900 border border-gray-700 rounded text-white"
              value={tolerance}
              onChange={(e) => setTolerance(e.target.value)}
              placeholder="e.g. 0.05 (in mm or unit you use)"
            />
          </div>
        </div>

        <div className="mb-6">
          <label className="block mb-2 text-cyan-300 font-medium">Remarks / Notes</label>
          <textarea
            className="w-full h-24 p-3 bg-gray-900 border border-gray-700 rounded text-white"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Instrument name, date, who participated, special conditions..."
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div>
            <label className="block mb-2 text-cyan-300 font-medium">Parts / Samples (one per line)</label>
            <textarea
              className="w-full h-40 p-3 bg-gray-900 border border-gray-700 rounded text-white font-mono text-sm"
              value={partsList}
              onChange={(e) => setPartsList(e.target.value)}
              placeholder="P1\nP2\nP3\n..."
            />
          </div>

          <div>
            <label className="block mb-2 text-cyan-300 font-medium">Operators / Appraisers</label>
            <textarea
              className="w-full h-40 p-3 bg-gray-900 border border-gray-700 rounded text-white font-mono text-sm"
              value={operatorsList}
              onChange={(e) => setOperatorsList(e.target.value)}
              placeholder="Op-A\nOp-B\nOp-C"
            />
          </div>

          <div>
            <label className="block mb-2 text-cyan-300 font-medium">Replications (measurements per part per operator)</label>
            <select
              className="w-full h-12 p-3 bg-gray-900 border border-gray-700 rounded text-white"
              value={replications}
              onChange={(e) => setReplications(e.target.value)}
            >
              <option value="2">2</option>
              <option value="3">3 (recommended)</option>
            </select>
          </div>
        </div>

        <div className="mb-6">
          <label className="block mb-2 text-cyan-300 font-medium">
            Measurement Data (one line per part – all readings separated by comma or space)
          </label>
          <textarea
            className="w-full h-48 p-3 bg-gray-900 border border-gray-700 rounded text-white font-mono text-sm"
            value={measurementDataText}
            onChange={(e) => setMeasurementDataText(e.target.value)}
            placeholder={
              "10.02, 10.03, 10.01,  10.04, 10.05, 10.03,  10.00, 10.01, 10.02\n" +
              "20.12, 20.14, 20.11,  20.15, 20.16, 20.13,  20.10, 20.09, 20.11\n" +
              "... (one line per part)"
            }
          />
        </div>

        <div className="flex justify-center mb-8">
          <button
            onClick={calculateRNR}
            className="w-48 py-2 bg-purple-700 hover:bg-purple-600 rounded-lg font-medium disabled:opacity-50"
            disabled={!title.trim() || !partsList.trim() || !operatorsList.trim() || !measurementDataText.trim()}
          >
            Calculate Gage R&R
          </button>
        </div>

        {error && (
          <div className="p-4 mb-6 bg-red-900/70 border border-red-700 rounded text-red-100 text-center">
            {error}
          </div>
        )}

        {rrResult && (
          <div className="mt-8 p-6 bg-gray-950 border border-gray-700 rounded-xl">
            <h2 className="text-2xl text-teal-300 mb-4 text-center">{rrResult.title}</h2>
            {rrResult.remarks && <p className="text-gray-300 mb-6 text-center italic">{rrResult.remarks}</p>}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                {renderBarContribution(rrResult)}

                <div className="mt-6 p-5 bg-gray-900 rounded-lg border border-gray-700">
                  <h3 className="text-xl text-teal-300 mb-4">Gage R&R Summary</h3>
                  <table className="w-full text-sm text-left text-gray-200">
                    <tbody>
                      <tr><td className="py-1 pr-4 font-medium">Equipment Variation (EV / Repeatability)</td><td>{rrResult.EV}</td></tr>
                      <tr><td className="py-1 pr-4 font-medium">Appraiser Variation (AV / Reproducibility)</td><td>{rrResult.AV}</td></tr>
                      <tr><td className="py-1 pr-4 font-medium">Total Gage R&R</td><td className="font-bold">{rrResult.gageRnr}</td></tr>
                      <tr><td className="py-1 pr-4 font-medium">Part-to-Part Variation</td><td>{rrResult.partToPart}</td></tr>
                      <tr><td className="py-1 pr-4 font-medium">Total Variation</td><td>{rrResult.totalVariation}</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="p-5 bg-gray-900 rounded-lg border border-gray-700">
                <h3 className="text-xl text-teal-300 mb-4">Key Results & Decision</h3>
                <div className="space-y-3">
                  <p><strong>% Gage R&R to Total Variation:</strong> <span className="text-2xl">{rrResult.gageRRPercent}%</span></p>
                  {rrResult.tolPercent && (
                    <p><strong>% Gage R&R to Tolerance:</strong> <span className="text-2xl">{rrResult.tolPercent}%</span></p>
                  )}
                  <p><strong>Number of Distinct Categories (ndc):</strong> <span className="text-2xl">{rrResult.ndc}</span></p>
                  <p className="pt-4 font-medium">
                    Interpretation: <span className={
                      rrResult.gageRRPercent <= 10 ? "text-green-400" :
                      rrResult.gageRRPercent <= 30 ? "text-yellow-300" : "text-red-400"
                    }>
                      {rrResult.interpretation}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-auto">
        <div className="max-w-6xl mx-auto px-4 flex flex-col lg:flex-row items-center justify-between gap-4 text-sm">
          <div className="text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions
          </div>
          <div className="flex flex-wrap gap-6">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SixSigmaRNR;